import { Box, useBoxStyle } from "arkh";
import React, { useCallback, useMemo, useRef, useState } from "react";

import { graphql, useFragment, useMutation } from "react-relay";
import { useShowCancelWarningAlert } from "../../../common/useCancelWarning";
import { KeyboardBottomInset } from "../../../components";
import { useModalInsets } from "../../../components/mobile/useModalInsets";
import { RoutineEditContentCard } from "./RoutineEditContentCard";
import { RoutineEditControlTypeCard } from "./RoutineEditControlTypeCard/RoutineEditControlTypeCard";
import { RoutineEditScreenProps } from "./RoutineEditNavigation";
import { RoutineEditScheduleCard } from "./RoutineEditScheduleCard/RoutineEditScheduleCard";
import { RoutineEditTeamListItem } from "./RoutineEditTeamListItem";
import { RoutineEditAssignedRolesListItem } from "./RoutineEditAssignedRolesListItem";
import { RoutineEditMainRoutine_Routine$key } from "./__generated__/RoutineEditMainRoutine_Routine.graphql";

import { useAlert } from "../../../common/Alert";
import { useIntl } from "react-intl";
import { RoutineEditMainDuplicateMutation } from "./__generated__/RoutineEditMainDuplicateMutation.graphql";
import { RoutineEditMainConvertToWeeklyMutation } from "./__generated__/RoutineEditMainConvertToWeeklyMutation.graphql";
import { useIntlTokens } from "../../../components/mobile/useIntlTokens";

import {
  MoreItem,
  useNavigationHeader,
} from "../../../common/navigationHeaderOptions";
import { ArchivedController } from "../../TeamAdmin/ArchivedController";
import { ArchivedCard } from "../../TeamAdmin/ArchivedCard";
import { RoutineEditMainConvertToWorkspaceTemplateMutation } from "./__generated__/RoutineEditMainConvertToWorkspaceTemplateMutation.graphql";
import { RoutineEditMainOrgTopicListItem_topics$key } from "./__generated__/RoutineEditMainOrgTopicListItem_topics.graphql";
import { RoutineEditOrgTopicListItem } from "./RoutineEditMainOrgTopicListItem";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";
import { useRoutineEditState } from "../State/RoutineEditState";
import { usePreventRemove } from "@react-navigation/native";
import { Alert, BackHandler, Platform } from "react-native";
import { useIsSavable } from "../State/useIsSaveable";
import { useRoutineEditDirty } from "../State/useRoutineEditDirty";
import { useSaveRoutineEdit } from "../State/useSaveRoutineEdit";

export function RoutineEditMain(
  props: {
    routine: RoutineEditMainRoutine_Routine$key;
    topics: RoutineEditMainOrgTopicListItem_topics$key | null;
  } & RoutineEditScreenProps<"RoutineEditMain">
) {
  const {
    route: { params },
    navigation: { goBack },
  } = props;
  const insets = useModalInsets();
  const routine = useFragment(
    graphql`
      fragment RoutineEditMainRoutine_Routine on Routine {
        id
        canBeUnarchived
        isArchived
        title
        type
        isOrg
        orgTopic {
          id
        }

        canBeConvertedToWorkspaceTemplate
        ...useIsSaveable_Routine
        ...RoutineEditContentCard_Routine
        ...RoutineEditScheduleCard_Routine
        ...RoutineEditTeamListItem_Routine
        ...useSaveRoutineEdit_routine
        ...useRoutineEditDirty_Routine
        ...RoutineEditAssignedRolesListItem_Routine
        canBeConvertedToWeekly
        currentSchedule {
          __typename
        }
      }
    `,
    props.routine
  );

  const [onDelete] = useMutation(graphql`
    mutation RoutineEditMainDeleteMutation($id: ID!) {
      routine(id: $id) {
        __typename
        delete
      }
    }
  `);

  const [onArchive] = useMutation(graphql`
    mutation RoutineEditMainArchiveMutation($id: ID!) {
      routine(id: $id) {
        archiveRoutine {
          id
          isArchived
          canBeUnarchived
        }
      }
    }
  `);

  const [onUnarchive] = useMutation(graphql`
    mutation RoutineEditMainUnarchiveMutation($id: ID!) {
      routine(id: $id) {
        unarchiveRoutine {
          id
          isArchived
        }
      }
    }
  `);

  const Alert = useAlert();
  const intl = useIntl();
  const intl_tokens = useIntlTokens();

  const routineEditState = useRoutineEditState();

  const title = useMemo(
    () => routineEditState.title ?? routine.title ?? "",
    [routine.title, routineEditState.title]
  );

  const onArchiveRoutineAlert = useCallback(async () => {
    Alert.alert(
      intl.formatMessage(
        {
          defaultMessage: `Archive "{title}"`,
          description: "onArchiveRoutineEventItem alert title",
        },
        {
          title,
        }
      ),
      intl.formatMessage(
        {
          defaultMessage: `Are you sure you want to archive "{title}"?`,
          description: "onArchiveRoutineEventItem alert description",
        },
        {
          title,
        }
      ),
      [
        {
          text: intl_tokens.Archive,
          style: "default",
          onPress: () => {
            onArchive({
              variables: {
                id: params.id,
              },
              onCompleted: () => {
                if (routine.isOrg) {
                  const navigateDeprecated = props.navigation
                    .navigateDeprecated as any;
                  navigateDeprecated("WorkspaceArchivedRoutines", {});
                } else {
                  goBack();
                }
              },
            });
          },
        },
        {
          text: intl_tokens.Cancel,
          style: "cancel",
        },
      ]
    );
  }, [
    Alert,
    intl,
    title,
    intl_tokens.Archive,
    intl_tokens.Cancel,
    onArchive,
    params.id,
    routine.isOrg,
    props.navigation.navigateDeprecated,
    goBack,
  ]);

  const onPressCancelWhenCreating = useCallback(() => {
    onDelete({
      variables: {
        id: params.id,
      },
      optimisticResponse: {
        routine: {
          __typename: "RoutineMutation",
          delete: true,
        },
      },
    });
  }, [onDelete, params.id]);

  const [selectedTopic, setSelectedTopic] = useState<string>(
    routine.orgTopic?.id ?? "NONE"
  );

  const topicDirty = selectedTopic !== (routine.orgTopic?.id ?? "NONE");

  const __dirty = useRoutineEditDirty({
    routine,
  });

  const _dirty = topicDirty || __dirty;

  const isSaveable =
    useIsSavable({
      routine,
      mode: params.mode,
    }) && _dirty;

  const hasSavedRef = useRef(false);

  const [onSave, loading] = useSaveRoutineEdit({
    onCompleted: () => {
      hasSavedRef.current = true;
      goBack();
    },
    routine,
    setOrgTopicId: selectedTopic ?? undefined,
  });

  const showWarn = useShowCancelWarningAlert();

  const preventRemove = params.mode === "CREATE" || isSaveable;
  usePreventRemove(preventRemove, ({ data }) => {
    if (hasSavedRef.current) {
      hasSavedRef.current = false;
      props.navigation.dispatch(data.action);

      return;
    }
    if (params.mode === "CREATE") {
      showWarn(() => {
        onPressCancelWhenCreating();
        props.navigation.dispatch(data.action);
      });
    } else {
      showWarn(() => {
        props.navigation.dispatch(data.action);
      });
    }
  });
  usePreventWebUnload(preventRemove);
  usePreventAndroidBackPress(preventRemove);

  const onPressCancel = useCallback(() => {
    goBack();
  }, [goBack]);

  const [convertToWeekly] = useMutation<RoutineEditMainConvertToWeeklyMutation>(
    graphql`
      mutation RoutineEditMainConvertToWeeklyMutation($id: ID!) {
        routine(id: $id) {
          convertToWeekly {
            routine {
              id
              currentSchedule {
                __typename
              }
            }
          }
        }
      }
    `
  );

  const [duplicate] = useMutation<RoutineEditMainDuplicateMutation>(graphql`
    mutation RoutineEditMainDuplicateMutation($id: ID!) {
      routine(id: $id) {
        duplicate {
          id
        }
      }
    }
  `);

  const [createWorkspaceTemplate] =
    useMutation<RoutineEditMainConvertToWorkspaceTemplateMutation>(graphql`
      mutation RoutineEditMainConvertToWorkspaceTemplateMutation($id: ID!) {
        routine(id: $id) {
          createWorkspaceTemplateFromRoutine {
            id
          }
        }
      }
    `);

  const moreItems: MoreItem[] = [];

  if (!routine.isArchived && params.mode !== "CREATE") {
    moreItems.push({
      label: intl_tokens.Archive,
      onPress: onArchiveRoutineAlert,
      icon: "archive",
      type: "button",
      color: "text-primary",
    });
  }
  const createWorkspaceTemplateTitle = intl.formatMessage({
    defaultMessage: "Add to workspace Routine Library",
    description: "Create Workspace Template Title",
  });

  if (routine.canBeConvertedToWorkspaceTemplate) {
    moreItems.push({
      label: createWorkspaceTemplateTitle,
      onPress() {
        Alert.alert(
          createWorkspaceTemplateTitle,
          intl.formatMessage({
            defaultMessage:
              "Do you want create a template from this routine in the workspace Routine Library?",
            description: "Create Workspace Template Alert Description",
          }),
          [
            {
              text: intl_tokens.OK,
              onPress: () => {
                createWorkspaceTemplate({
                  variables: {
                    id: params.id,
                  },
                  onCompleted: () => {
                    props.navigation.pop();
                  },
                });
              },
            },
            {
              text: intl_tokens.Cancel,
              isPreferred: true,
              style: "default",
            },
          ]
        );
      },
      icon: "template",
      type: "button",
    });
  }
  if (
    routine.currentSchedule?.__typename === "RoutineScheduleDaily" &&
    routine.canBeConvertedToWeekly
  ) {
    moreItems.push({
      label: intl.formatMessage({
        defaultMessage: "Convert to weekly",
        description: "Convert to weekly alert title",
      }),
      onPress: () => {
        Alert.alert(
          intl.formatMessage({
            defaultMessage: "Convert to weekly",
            description: "Convert to weekly alert title",
          }),
          intl.formatMessage({
            defaultMessage:
              "This will convert your routine to a weekly routine. Weekly routines can only be scheduled once a week. Are you sure that you want to continue? ",
          }),
          [
            {
              text: intl_tokens.OK,
              onPress: () => {
                convertToWeekly({
                  variables: {
                    id: params.id,
                  },
                  onCompleted: ({ routine }) => {
                    props.navigation.replace("RoutineEditMain", {
                      id: routine?.convertToWeekly?.routine?.id ?? "",
                      mode: undefined,
                      orgWorkspaceId: params.orgWorkspaceId,
                    });
                  },
                });
              },
            },
            {
              text: intl_tokens.Cancel,
              isPreferred: true,
              style: "default",
            },
          ]
        );
      },
      type: "button",
      color: "neutral",
    });
  }

  if (params.mode !== "CREATE") {
    moreItems.push({
      label: intl_tokens.Duplicate,
      onPress: () => {
        duplicate({
          variables: {
            id: params.id,
          },
          onCompleted: ({ routine }) => {
            goBack();
          },
          onError: (error) => {
            Alert.alert("Error duplicating routine", error.message);
          },
        });
      },
      type: "button",
      icon: "duplicate",
    });
  }

  useNavigationHeader(
    routine.isArchived
      ? {
          type: "CLOSE",
          defaultContentStyle: true,
          onClose: () => {
            goBack();
          },
        }
      : {
          type: "SAVE",
          disabled: !isSaveable,
          defaultContentStyle: true,
          onSave,
          onCancel: onPressCancel,
          saveLoading: loading,
          more: moreItems.length > 0 ? moreItems : undefined,
        }
  );

  const contentContainerStyle = useBoxStyle({
    space: "medium",
    insets: { bottom: insets.bottom },
  });

  return (
    <KeyboardAwareScrollView
      keyboardShouldPersistTaps="handled"
      contentContainerStyle={contentContainerStyle}
    >
      <ArchivedController
        archived={routine.isArchived ?? false}
        archivedCard={
          <ArchivedCard
            loading={false}
            title={intl_tokens.Archived}
            onPress={
              routine.canBeUnarchived
                ? () => {
                    onUnarchive({
                      variables: {
                        id: params.id,
                      },
                      onCompleted: () => {
                        if (routine.isOrg) {
                          const navigateDeprecated = props.navigation
                            .navigateDeprecated as any;
                          navigateDeprecated("WorkspaceComplianceRoutines", {});
                        } else {
                          goBack();
                        }
                      },
                    });
                  }
                : undefined
            }
            unarchiveTitle={intl_tokens.Unarchive}
          />
        }
      >
        <RoutineEditContentCard
          routine={routine}
          navigation={props.navigation}
          route={props.route}
        />
        <Box spaceTop="medium" />
        <RoutineEditControlTypeCard
          mode={params.mode ?? "EDIT"}
          isOrg={routine.isOrg}
          navigation={props.navigation}
          routineId={routine.id}
          orgWorkspaceId={params.orgWorkspaceId}
        />
        {props.topics ? (
          <>
            <Box spaceTop="medium" />
            <RoutineEditOrgTopicListItem
              topics={props.topics}
              selectedTopic={selectedTopic ?? "NONE"}
              onSelectedTopicChange={setSelectedTopic}
            />
          </>
        ) : null}
        {routine.isArchived ? null : (
          <>
            <Box spaceTop="medium" />
            <RoutineEditScheduleCard
              routine={routine}
              navigation={props.navigation}
              route={props.route}
            />
          </>
        )}
        <Box spaceTop="medium" />
        <RoutineEditAssignedRolesListItem routine={routine} />
        {params.mode === "CREATE" ? null : (
          <>
            <Box spaceTop="medium" />
            <RoutineEditTeamListItem
              routine={routine}
              navigation={props.navigation}
              route={props.route}
            />
          </>
        )}
        <KeyboardBottomInset />
      </ArchivedController>
    </KeyboardAwareScrollView>
  );
}

function usePreventWebUnload(preventRemove: boolean) {
  React.useEffect(() => {
    if (Platform.OS !== "web") {
      return;
    }
    if (!preventRemove) {
      return;
    }
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      // Prevent the user from leaving the page

      event.preventDefault();
      event.returnValue = true;
    };

    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [preventRemove]);
}

function usePreventAndroidBackPress(preventRemove: boolean) {
  React.useEffect(() => {
    if (Platform.OS !== "android") {
      return;
    }
    if (!preventRemove) {
      return;
    }
    const onBackPress = () => {
      Alert.alert(
        "Exit App",
        "Do you want to exit?",
        [
          {
            text: "Cancel",
            onPress: () => {},
            style: "cancel",
          },
          { text: "YES", onPress: () => BackHandler.exitApp() },
        ],
        { cancelable: false }
      );

      return true;
    };

    const backHandler = BackHandler.addEventListener(
      "hardwareBackPress",
      onBackPress
    );

    return () => backHandler.remove();
  }, [preventRemove]);
}
